import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { epochLabel } from '@/features/projects/format'
import { selectSessionPrincipalKey } from '@/features/auth/slices/sessionSlice'
import { selectProjectMembers } from '@/features/projects/slices/projectDetailSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { callerSide } from '../callerSide'
import { MemberSelect } from './MemberSelect'
import {
  loadGoNoGo,
  recordGoNoGo,
  selectGoNoGo,
  selectGoNoGoError,
  selectGoNoGoLoading,
  selectGoNoGoMissing,
  selectMigrationMutating,
  selectMigrationNonce,
  toDisplayError
} from '../slices/migrationSlice'
import type { GoNoGoVerdict } from '../types'

const VERDICT_TONE: Record<GoNoGoVerdict, StatusTone> = {
  GO: 'done',
  NO_GO: 'blocked',
  GO_WITH_CONDITIONS: 'review'
}

const VERDICT_OPTIONS: readonly GoNoGoVerdict[] = ['GO', 'NO_GO', 'GO_WITH_CONDITIONS']

interface ConditionDraft {
  key: string
  text: string
  ownerUserId: string
  dueAt: string
}

/**
 * The go/no-go panel (AC-5) — self-contained. Never a bare tick: the
 * verdict is always rendered with the decider's name. The record form is
 * offered only to a client-side caller (Decision 7); the server's own gate
 * is the real refusal either way, so a firm-side caller who somehow submits
 * still sees the 403 rendered inline, never a claimed recording.
 */
export function GoNoGoPanel({ orgId, projectId, planId }: { orgId: string; projectId: string; planId: string | null }) {
  const { t } = useTranslation('projectWorkspace')
  const { t: tCommon } = useTranslation('common')
  const dispatch = useAppDispatch()
  const record = useAppSelector(selectGoNoGo)
  const loading = useAppSelector(selectGoNoGoLoading)
  const error = useAppSelector(selectGoNoGoError)
  const missing = useAppSelector(selectGoNoGoMissing)
  const mutating = useAppSelector(selectMigrationMutating)
  const nonce = useAppSelector(selectMigrationNonce)
  const members = useAppSelector(selectProjectMembers)
  const principalKey = useAppSelector(selectSessionPrincipalKey)

  const isClientSide = useMemo(() => callerSide(members, principalKey) === 'CLIENT', [members, principalKey])

  // Finding 1 (AC-1): `goNoGo.error` carries both the primary READ failure
  // (`scope` undefined) and a RECORD refusal (`scope: 'record'`) — only the
  // former replaces the panel with `<ErrorPanel>`; the latter is rendered
  // inline so a refused record never hides the stored decision.
  const readError = error !== null && error.scope === undefined ? error : null
  const recordError = error !== null && error.scope === 'record' ? error : null

  const [showForm, setShowForm] = useState(false)
  const [verdict, setVerdict] = useState<GoNoGoVerdict>('GO')
  const [rationale, setRationale] = useState('')
  const [conditions, setConditions] = useState<ConditionDraft[]>([])

  useEffect(() => {
    if (planId === null) return
    void dispatch(loadGoNoGo({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId, nonce])

  const onRetryClicked = useCallback(() => {
    if (planId === null) return
    void dispatch(loadGoNoGo({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId])

  const onAddConditionClicked = useCallback(() => {
    setConditions((rows) => [...rows, { key: `c-${rows.length}-${Date.now()}`, text: '', ownerUserId: '', dueAt: '' }])
  }, [])

  const onRemoveConditionClicked = useCallback((key: string) => {
    setConditions((rows) => rows.filter((r) => r.key !== key))
  }, [])

  const onSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (planId === null) return
      void dispatch(
        recordGoNoGo({
          orgId,
          projectId,
          planId,
          input: {
            verdict,
            rationale,
            conditions:
              verdict === 'GO_WITH_CONDITIONS'
                ? conditions.map((c) => ({
                    text: c.text,
                    ownerUserId: c.ownerUserId,
                    dueAt: c.dueAt === '' ? 0 : new Date(c.dueAt).getTime()
                  }))
                : undefined
          }
        })
      ).then((result) => {
        if (recordGoNoGo.fulfilled.match(result)) {
          setShowForm(false)
          setRationale('')
          setConditions([])
        }
      })
    },
    [dispatch, orgId, projectId, planId, verdict, rationale, conditions]
  )

  if (planId === null) {
    return (
      <section aria-label={t('migration.goNoGo.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
        <SectionLabel>{t('migration.goNoGo.title')}</SectionLabel>
        <div className="mt-2">
          <EmptyPanel title={t('migration.goNoGo.noPlanTitle')} body={t('migration.goNoGo.noPlanBody')} />
        </div>
      </section>
    )
  }

  return (
    <section
      aria-label={t('migration.goNoGo.title')}
      className="rounded-lg border border-border bg-card px-4 py-3.5"
      data-testid="migration-go-no-go-panel"
    >
      <SectionLabel>{t('migration.goNoGo.title')}</SectionLabel>

      {recordError !== null && (
        <div role="alert" data-testid="go-no-go-record-error" className="mt-2 rounded-md border border-border bg-card px-3 py-2">
          <p className="text-xs font-medium text-blocked">{t('migration.goNoGo.recordRefusedLabel')}</p>
          {/* Fix cycle 1 (GRP-82297E9A, deferred visual finding): the server's
              reason for a refused record (e.g. a 409 differences-open
              conflict) has no translated copy today, so the mapped message
              always fell back to the generic "Something went wrong. Try
              again." — indistinguishable from a transient outage and wrongly
              promising a retry would help. Status/Code (C-4's own dl shape,
              `common:states.statusLabel`/`codeLabel`) carry the real,
              distinguishing signal instead. */}
          {(recordError.status > 0 || recordError.code !== undefined) && (
            <dl className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
              {recordError.status > 0 && (
                <div className="flex items-center gap-1">
                  <dt>{tCommon('states.statusLabel')}</dt>
                  <dd data-testid="go-no-go-record-error-status" className="v-figure tabular-nums text-foreground">
                    {recordError.status}
                  </dd>
                </div>
              )}
              {recordError.code !== undefined && (
                <div className="flex items-center gap-1">
                  <dt>{tCommon('states.codeLabel')}</dt>
                  <dd data-testid="go-no-go-record-error-code" className="font-mono text-foreground">
                    {recordError.code}
                  </dd>
                </div>
              )}
            </dl>
          )}
          {recordError.status <= 0 && recordError.code === undefined && (
            <p data-testid="go-no-go-record-error-message" className="mt-1 text-xs text-foreground">
              {recordError.message}
            </p>
          )}
        </div>
      )}

      {readError !== null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(readError)} onRetryClicked={onRetryClicked} />
        </div>
      ) : loading && record === null && !missing ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.goNoGo.loading')} />
        </div>
      ) : record !== null ? (
        <div className="mt-2 rounded-lg border border-border px-4 py-3" data-testid="go-no-go-record">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={VERDICT_TONE[record.verdict]}>{t(`migration.verdict.${record.verdict}`)}</StatusPill>
            <span className="text-sm font-medium text-foreground">
              {t('migration.goNoGo.decidedBy', { name: record.decidedByUserName })}
            </span>
            <span className="text-xs text-muted-foreground">{epochLabel(record.decidedAt)}</span>
          </div>
          <p className="mt-2 text-sm text-foreground">{record.rationale}</p>
          {record.conditions.length > 0 && (
            <ul className="mt-2">
              {record.conditions.map((c, i) => (
                <li key={i} className="v-row py-1 text-xs text-muted-foreground">
                  {c.text} — {c.ownerName ?? c.ownerUserId} — {epochLabel(c.dueAt)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-2">
          <EmptyPanel title={t('migration.goNoGo.emptyTitle')} body={t('migration.goNoGo.emptyBody')} />
        </div>
      )}

      {isClientSide && (
        <div className="mt-3">
          {!showForm ? (
            <Button type="button" variant="outline" onClick={() => setShowForm(true)}>
              {t('migration.goNoGo.recordAction')}
            </Button>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
              <div>
                <label htmlFor="go-no-go-verdict" className="text-xs font-medium text-foreground">
                  {t('migration.goNoGo.form.verdict')}
                </label>
                <select
                  id="go-no-go-verdict"
                  value={verdict}
                  onChange={(e) => setVerdict(e.target.value as GoNoGoVerdict)}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-card px-2 text-sm text-foreground"
                >
                  {VERDICT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t(`migration.verdict.${option}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="go-no-go-rationale" className="text-xs font-medium text-foreground">
                  {t('migration.goNoGo.form.rationale')}
                </label>
                <Input
                  id="go-no-go-rationale"
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              {verdict === 'GO_WITH_CONDITIONS' && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-foreground">{t('migration.goNoGo.form.conditions')}</span>
                  {conditions.map((c) => (
                    <div key={c.key} className="flex flex-wrap items-end gap-2">
                      <Input
                        aria-label={t('migration.goNoGo.form.conditionText')}
                        value={c.text}
                        onChange={(e) =>
                          setConditions((rows) => rows.map((r) => (r.key === c.key ? { ...r, text: e.target.value } : r)))
                        }
                        required
                        className="min-w-[10rem] flex-1"
                      />
                      <MemberSelect
                        id={`go-no-go-condition-owner-${c.key}`}
                        aria-label={t('migration.goNoGo.form.conditionOwner')}
                        members={members}
                        value={c.ownerUserId}
                        onChange={(value) =>
                          setConditions((rows) => rows.map((r) => (r.key === c.key ? { ...r, ownerUserId: value } : r)))
                        }
                        required
                        className="w-40"
                      />
                      <Input
                        type="date"
                        aria-label={t('migration.goNoGo.form.conditionDue')}
                        value={c.dueAt}
                        onChange={(e) =>
                          setConditions((rows) => rows.map((r) => (r.key === c.key ? { ...r, dueAt: e.target.value } : r)))
                        }
                        required
                        className="w-40"
                      />
                      <Button type="button" size="sm" variant="outline" onClick={() => onRemoveConditionClicked(c.key)}>
                        {t('migration.goNoGo.form.removeCondition')}
                      </Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" onClick={onAddConditionClicked}>
                    {t('migration.goNoGo.form.addCondition')}
                  </Button>
                </div>
              )}
              <div className="flex gap-2">
                <Button type="submit" disabled={mutating === 'go-no-go-record'}>
                  {t('migration.goNoGo.form.submit')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  {t('migration.goNoGo.form.cancel')}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  )
}
